import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { TermsMarkdown } from "../components/terms/TermsMarkdown";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useTermsDraftStore } from "../store/useTermsDraftStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useTerms } from "../hooks/useTerms";
import { acceptTerms } from "../services/api";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";

const EMPTY_CHECKS: Record<string, boolean> = {};

/**
 * Welcome / consent screen. Shown after the splash when the user has not
 * accepted the current terms version (`!termsAccepted`); otherwise the flow
 * goes straight to the first screen.
 *
 * When the partner has configured Terms & Conditions entries
 * (`session.data.terms`), they are rendered dynamically — one checkbox per
 * entry with a markdown intro and a navigable detail screen. Otherwise the
 * screen falls back to the legacy E-Sign + Terms checkboxes.
 */
export const WelcomePage = memo(() => {
  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const { session, updateSession } = useSessionStore();
  const { redirectTo } = useRouteWithToken();

  // Keep terms localized to the active UI language (English fallback).
  useTerms();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, dropzoneColors, backgroundIcon } = colorConfig;

  const linkId = session.data?.linkId;

  const terms = session.data?.terms;
  const entries = terms?.entries ?? [];
  const hasDynamicTerms = entries.length > 0;

  // Survives navigation to a legal document and back. Scoped to this link.
  const draftScope = useTermsDraftStore((s) => s.scope);
  const storedCheckedById = useTermsDraftStore((s) => s.checkedById);
  const storedESign = useTermsDraftStore((s) => s.isESignChecked);
  const storedTerms = useTermsDraftStore((s) => s.isTermsChecked);
  const setEntryChecked = useTermsDraftStore((s) => s.setEntryChecked);
  const setESignChecked = useTermsDraftStore((s) => s.setESignChecked);
  const setTermsChecked = useTermsDraftStore((s) => s.setTermsChecked);
  const resetTermsDraft = useTermsDraftStore((s) => s.reset);
  const draftActive = !!linkId && draftScope === linkId;
  const checkedById = draftActive ? storedCheckedById : EMPTY_CHECKS;
  const isESignChecked = draftActive && storedESign;
  const isTermsChecked = draftActive && storedTerms;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    if (!session.data?.termsAccepted) return;
    redirectTo(APP_ROUTES.LANDING_PAGE, { replace: true });
  }, [session.data?.termsAccepted, redirectTo]);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("welcome"));
  }, []);

  const allRequiredAccepted = useMemo(() => {
    if (hasDynamicTerms) {
      return entries
        .filter((entry) => entry.required !== false)
        .every((entry) => checkedById[entry.id]);
    }
    return isESignChecked && isTermsChecked;
  }, [hasDynamicTerms, entries, checkedById, isESignChecked, isTermsChecked]);

  const handleContinue = async () => {
    if (isSubmitting || !allRequiredAccepted) return;
    setIsSubmitting(true);
    setSubmitError(false);
    safeCapture(getEvents().ACCEPT_BUTTON_CLICK);

    try {
      if (!linkId) throw new Error("TERMS_ACCEPT_FAILED");
      if (hasDynamicTerms) {
        const acceptedEntryIds = entries
          .filter((entry) => checkedById[entry.id])
          .map((entry) => entry.id);
        await acceptTerms(linkId, {
          version: terms?.version,
          acceptedEntryIds,
          language: i18n.language,
        });
      } else {
        await acceptTerms(linkId, {
          documents: ["esign", "userAgreement"],
          language: i18n.language,
        });
      }
      if (session.data) {
        updateSession({ ...session.data, termsAccepted: true });
      }
      resetTermsDraft();
      safeCapture(getEvents().TERMS_ACCEPTED, { success: true });
      redirectTo(APP_ROUTES.LANDING_PAGE);
    } catch {
      safeCapture(getEvents().TERMS_ACCEPTED, {
        success: false,
        error_code: "TERMS_ACCEPT_FAILED",
      });
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  return (
    <AuraLayout colorConfig={colorConfig}>
      <Layout.Content className="landing-section welcome-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">

          <FlowHeroMark backgroundIcon={backgroundIcon}>

            <CashSpecialIcon size={60} colors={dropzoneColors} />

          </FlowHeroMark>
        <section className="landing-container">
          <PageTitle
            highlight={t("welcome.highlight")}
            highlightColor={TITLE_COLOR}
            secudnary={` ${t("welcome.secondary")}`}
            secudnaryColor={TITLE_COLOR}
          />
        </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          {hasDynamicTerms ? (
            entries.map((entry) => (
              <section className="landing-policies" key={entry.id}>
                <span
                  className={`policy-checkbox${
                    entry.required !== false && !checkedById[entry.id]
                      ? " policy-checkbox--required"
                      : ""
                  }`}
                >
                  <input
                    id={`term-${entry.id}`}
                    type="checkbox"
                    checked={!!checkedById[entry.id]}
                    onChange={(e) => {
                      if (!linkId) return;
                      setEntryChecked(linkId, entry.id, e.target.checked);
                    }}
                  />
                </span>
                <div className="footer-text terms-text footer-link muted-foreground">
                  <label htmlFor={`term-${entry.id}`}>
                    <TermsMarkdown content={entry.introMarkdown} inline />
                  </label>{" "}
                  {entry.detailMarkdown && (
                    <GradientText
                      as="label"
                      className="link"
                      textColor={title}
                      onClick={() =>
                        redirectTo(
                          `${APP_ROUTES.TERM_DETAIL_PAGE}?entryId=${entry.id}`
                        )
                      }
                    >
                      {t("terms.viewDetails")}
                    </GradientText>
                  )}
                </div>
              </section>
            ))
          ) : (
            <>
              <section className="landing-policies">
                <span
                  className={`policy-checkbox${
                    isESignChecked ? "" : " policy-checkbox--required"
                  }`}
                >
                  <input
                    id="welcome-e-sign"
                    type="checkbox"
                    checked={isESignChecked}
                    onChange={(e) => {
                      if (!linkId) return;
                      setESignChecked(linkId, e.target.checked);
                    }}
                  />
                </span>
                <BodyCopy className="footer-text terms-text footer-link muted-foreground">
                  <label htmlFor="welcome-e-sign">
                    {t("welcome.esignContent")}
                  </label>
                  <GradientText
                    as="label"
                    className="link"
                    textColor={title}
                    onClick={() =>
                      redirectTo(`${APP_ROUTES.LEGAL_PAGE}?doc=esign`)
                    }
                  >
                    {t("welcome.esignLink")}
                  </GradientText>
                </BodyCopy>
              </section>

              <section className="landing-policies">
                <span
                  className={`policy-checkbox${
                    isTermsChecked ? "" : " policy-checkbox--required"
                  }`}
                >
                  <input
                    id="welcome-terms"
                    type="checkbox"
                    checked={isTermsChecked}
                    onChange={(e) => {
                      if (!linkId) return;
                      setTermsChecked(linkId, e.target.checked);
                    }}
                  />
                </span>
                <BodyCopy className="footer-text terms-text footer-link muted-foreground">
                  <label htmlFor="welcome-terms">
                    {t("welcome.termsContent")}
                  </label>
                  <GradientText
                    as="label"
                    className="link"
                    textColor={title}
                    onClick={() =>
                      redirectTo(`${APP_ROUTES.LEGAL_PAGE}?exclude=esign`)
                    }
                  >
                    {t("welcome.termsLink")}
                  </GradientText>
                  <label>{t("welcome.termsContent2")}</label>
                </BodyCopy>
              </section>
            </>
          )}

          {submitError && (
            <BodyCopy className="footer-text" textColor={bodyCopy}>
              {t("error.tryAgainLater")}
            </BodyCopy>
          )}
          <Button
            size="large"
            disabled={!allRequiredAccepted || isSubmitting}
            background={backgroundBtn}
            color={textColorBtn}
            onClick={handleContinue}
          >
            {t("welcome.accept")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
